<script setup lang="ts">
import { ref, computed } from "vue"
import { Button } from "@/components/ui/button"
import { useI18n } from 'vue-i18n';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'

const { t } = useI18n();

import type { PropType } from 'vue'

const props = defineProps({
  col: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  selectedOptions: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  dynamicOptions: { type: Object as PropType<Record<string, any>>, default: () => ({}) },
  toggleOption: {
    type: Function as PropType<(oid: number, val: any) => void>,
    required: true,
  },
})

const searchQuery = ref('')

// Get the full list of options
const allOptions = computed(() => props.col.options || props.dynamicOptions[props.col.key] || [])

// Only show search if more than 8 entries
const showSearch = computed(() => allOptions.value.length > 8)

// Computed property dynamically filters options as user types
const filteredOptions = computed(() => {
  if (!searchQuery.value || !showSearch.value) return allOptions.value
  return allOptions.value.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})
</script>

<template>
<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button size="sm" variant="outline" class="w-full">
      {{ Object.keys(selectedOptions).length > 0 ? Object.values(selectedOptions).join(', ') : t('view.all') }}
    </Button>
  </DropdownMenuTrigger>

  <DropdownMenuContent class="w-60 max-h-64 overflow-y-auto z-[9999]">
    <DropdownMenuLabel>{{ col.label }}</DropdownMenuLabel>

    <!-- Show search only if more than 8 options -->
    <div v-if="showSearch" class="p-2">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Suche..."
        class="w-full px-2 py-1 border rounded focus:outline-none focus:ring focus:ring-blue-300"
        @keydown.stop
        @click.stop
      />
    </div>

    <DropdownMenuGroup>
      <DropdownMenuItem
        v-for="opt in filteredOptions"
        :key="opt.id"
        class="flex items-center gap-2 px-2 py-1 cursor-pointer"
        :style="{ color: opt.color || 'inherit', backgroundColor: opt.bgcolor || 'inherit' }"
        @select.prevent 
        @click.stop="toggleOption(opt.id, !selectedOptions[opt.id])"
      >
        <input
          type="checkbox"
          :checked="!!selectedOptions[opt.id]"
          class="accent-blue-500 pointer-events-none"
        />
        <span>{{ opt.label }}</span>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  </DropdownMenuContent>
</DropdownMenu>
</template>
